import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@jablotron/lib.spa-components';
import { useTranslate } from '@jablotron/lib.spa-lang';
import { Link } from '@tanstack/react-router';
import { type PointerEvent, useEffect, useRef, useState } from 'react';
import { getLineIconComponent, getLineIconSrc } from './megaMenuIcons';
import { getCatalogSync } from '~/entities/catalog/api';
import { CANONICAL_CATEGORIES, canonicalCategoryTitle } from '~/entities/catalog/canonical-categories';
import { Texts } from '~/texts/runtime';

/**
 * Pointer leaving the trigger/content schedules a close; re-entering either cancels it.
 * Generous enough to cover normal mouse transit from the trigger into the content —
 * too short and the menu flickers closed-then-reopen while the pointer is still between them.
 */
const CLOSE_DELAY_MS = 250;

/** Short tagline under each "Produktová řada" item title — live text, so it localizes to English too. */
const LINE_TAGLINES: Record<string, string> = {
  'jablotron-100-plus': Texts.MegaMenuTaglineFlexiAlarm,
  'jablotron-mercury': Texts.MegaMenuTaglineMercuryAlarm,
  nanny: Texts.MegaMenuTaglineNanny,
  'mobile-control': Texts.MegaMenuTaglineMobileControl,
  'grade-3': Texts.MegaMenuTaglineGrade3,
  'software-and-applications': Texts.MegaMenuTaglineSoftwareAndApplications,
};

type MegaMenuCategoryItem = {
  slug: string;
  title: string;
};

type MegaMenuLineItem = {
  slug: string;
  title: string;
};

function useCategoryItems(): MegaMenuCategoryItem[] {
  const catalog = getCatalogSync();

  return CANONICAL_CATEGORIES.map((category) => ({
    slug: category.slug,
    title: canonicalCategoryTitle(category, catalog.subcategoryPages),
  }));
}

function useLineItems(): MegaMenuLineItem[] {
  const catalog = getCatalogSync();

  return catalog.categories.map((category) => ({ slug: category.slug, title: category.title }));
}

/**
 * Two-column nav dropdown: "Produktová řada" (6) + "Produktové kategorie" (16). With a mouse it opens on
 * hover and clicking "Produkty" goes to /products; on touch (e.g. an iPad in landscape, which gets the
 * desktop header) there is no hover, so a tap only toggles the menu and never navigates — on touch,
 * /products is reached via a category or line item instead, per the "no additional link" spec (see
 * products-mega-menu.md's acceptance criteria).
 */
export function MegaMenu() {
  const t = useTranslate();
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const lastPointerType = useRef('');
  const categoryItems = useCategoryItems();
  const lineItems = useLineItems();

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  // Touch devices fire pointerenter/pointerleave around every tap too — hover behaviour is mouse-only.
  const openMenu = (event: PointerEvent) => {
    if (event.pointerType !== 'mouse') {
      return;
    }
    clearTimeout(closeTimer.current);
    setOpen(true);
  };

  const scheduleClose = (event: PointerEvent) => {
    if (event.pointerType !== 'mouse') {
      return;
    }
    closeTimer.current = setTimeout(() => setOpen(false), CLOSE_DELAY_MS);
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen} modal={false}>
      <DropdownMenuTrigger asChild>
        <Link
          to="/products"
          onPointerEnter={openMenu}
          onPointerLeave={scheduleClose}
          onPointerDown={(event) => {
            lastPointerType.current = event.pointerType;
          }}
          onClick={(event) => {
            // The trigger's pointerdown already toggled the menu; on touch, stay put instead of navigating.
            if (lastPointerType.current && lastPointerType.current !== 'mouse') {
              event.preventDefault();

              return;
            }
            setOpen(false);
          }}
          className="whitespace-nowrap border-none bg-transparent p-0 text-sm font-normal text-brand-muted no-underline transition-colors hover:text-brand-ink"
        >
          {t(Texts.NavProducts)}
        </Link>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="start"
        sideOffset={0}
        onPointerEnter={openMenu}
        onPointerLeave={scheduleClose}
        className="w-[1040px] max-w-[92vw] rounded-catalog-lg border-primary p-6 shadow-md"
      >
        <div className="grid grid-cols-[auto_1fr]">
          <div data-testid="mega-menu-lines" className="border-r border-primary pr-8">
            <h3 className="mb-4 mt-0 text-xl font-semibold text-brand-ink">{t(Texts.MegaMenuLinesHeading)}</h3>
            <ul className="m-0 flex list-none flex-col gap-3 p-0">
              {lineItems.map((item) => {
                const LineIcon = getLineIconComponent(item.slug);

                return (
                  <li key={item.slug}>
                    <Link
                      to="/categories/$categoryId"
                      params={{ categoryId: item.slug }}
                      onClick={() => setOpen(false)}
                      className="flex items-center gap-3 rounded-md px-2 py-1.5 no-underline hover:bg-brand-surface"
                    >
                      {LineIcon ? (
                        <LineIcon width={20} height={20} className="shrink-0 text-brand-cyan" />
                      ) : (
                        <img src={getLineIconSrc(item.slug)} alt="" width={20} height={20} className="shrink-0" />
                      )}
                      <span>
                        <span className="block text-base font-semibold text-brand-ink">{t(item.title)}</span>
                        <span className="block text-small text-brand-muted">{t(LINE_TAGLINES[item.slug])}</span>
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>

          <div data-testid="mega-menu-categories" className="pl-8">
            <h3 className="mb-4 mt-0 text-xl font-semibold text-brand-ink">{t(Texts.MegaMenuCategoriesHeading)}</h3>
            <ul className="m-0 grid list-none grid-cols-2 gap-x-4 gap-y-1 p-0">
              {categoryItems.map((item) => (
                <li key={item.slug}>
                  <Link
                    to="/products"
                    search={{ category: item.slug }}
                    onClick={() => setOpen(false)}
                    className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm leading-snug text-brand-ink no-underline hover:bg-brand-surface hover:text-brand-cyanText"
                  >
                    <span>{t(item.title)}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
